import TaskItem from '@tiptap/extension-task-item'
import type { Node as PMNode } from 'prosemirror-model'
import { uuid } from 'vue-uuid'
import i18n from '@/util/config/i18n'

/** What a screen reader says for a checkbox: the item's own text, so "Buy milk, checkbox, checked". */
export function taskItemLabel(node: PMNode): string {
  return node.firstChild?.textContent.trim() ?? ''
}

export const CustomTaskItem = TaskItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        parseHTML: element => element.getAttribute('data-task-id'),
        renderHTML: attributes => {
          return {
            'data-task-id': attributes.id ? attributes.id : uuid.v4()
          }
        },
        keepOnSplit: false
      }
    }
  },

  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
      // Keyboard counterpart of clicking the checkbox, which is not a tab stop (see addNodeView)
      'Mod-Enter': () => {
        if (!this.editor.isEditable) return false
        const { $from } = this.editor.state.selection
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth)
          if (node.type.name !== this.name) continue
          return this.editor.commands.command(({ tr }) => {
            tr.setNodeMarkup($from.before(depth), undefined, { ...node.attrs, checked: !node.attrs.checked })
            return true
          })
        }
        return false
      }
    }
  },

  addNodeView() {
    const parentView = this.parent?.()
    return props => {
      const view = parentView!(props)
      const box = (view.dom as HTMLElement).querySelector('input[type="checkbox"]')
      // Roving access while editing: Tab moves through the document, not through every checkbox, and Mod-Enter
      // toggles from the text. A read-only checklist has no text caret, so its checkboxes stay tab stops.
      const syncTabIndex = () => {
        // Runs on every editor update: touch the DOM only when the state flipped
        const roving = box?.getAttribute('tabindex') === '-1'
        if (this.editor.isEditable === roving) return
        if (this.editor.isEditable) box?.setAttribute('tabindex', '-1')
        else box?.removeAttribute('tabindex')
      }
      syncTabIndex()
      this.editor.on('update', syncTabIndex)
      const name = (node: PMNode) => {
        const label = taskItemLabel(node) || (i18n.t('elementPlaceholder.taskItemLabel') as string)
        if (box && box.getAttribute('aria-label') !== label) box.setAttribute('aria-label', label)
      }
      name(props.node)
      const destroy = view.destroy
      view.destroy = () => {
        this.editor.off('update', syncTabIndex)
        destroy?.()
      }
      const update = view.update
      view.update = (node, ...rest) => {
        const ok = update ? update(node, ...rest) : false
        if (ok) name(node)
        return ok
      }
      return view
    }
  }
})
